import {
  CANVAS_USES_POINTER_EVENTS,
  normalizePointerEventForCanvas
} from './utils/pointer-input.utils';

let callEvent = function (eventName, e) {
  if (!this._enable) {
    return;
  }

  let i = 0,
    component,
    componentEvent,
    breaker;
  for (; i < this._componentsCount; i++) {
    component = this.components[i];
    componentEvent = component[eventName];
    if (componentEvent && component.isEnabled()) {
      breaker = componentEvent.call(component, e);
      if (breaker === false) {
        return;
      }
    }
  }
};

let prepareEventFuncs = function () {
  const _this = this;
  this._pointerEventMode = CANVAS_USES_POINTER_EVENTS;
  this._activeTouchPointers = this._activeTouchPointers || {};
  /** Активные контакты Touch Events на canvas (режим без pointer-only). */
  this._canvasTouchContactCount = 0;


  const hasAnyActiveCanvasTouch = function () {
    if (_this._canvasTouchContactCount > 0) {
      return true;
    }
    if (!_this._activeTouchPointers) {
      return false;
    }
    for (const k in _this._activeTouchPointers) {
      if (_this._activeTouchPointers.hasOwnProperty(k) && _this._activeTouchPointers[k]) {
        return true;
      }
    }
    return false;
  };

  const markTouchPointer = function (e, active) {
    if (!e || e.pointerType !== 'touch') {
      return;
    }
    if (typeof e.pointerId !== 'number') {
      return;
    }
    if (!_this._activeTouchPointers) {
      _this._activeTouchPointers = {};
    }
    if (active) {
      _this._activeTouchPointers[e.pointerId] = true;
    } else if (_this._activeTouchPointers) {
      delete _this._activeTouchPointers[e.pointerId];
    }
  };

  const adjustCanvasTouchContacts = function (e: TouchEvent, sign: 1 | -1) {
    if (_this._pointerEventMode || !e || !e.changedTouches) {
      return;
    }
    _this._canvasTouchContactCount = Math.max(
      0,
      _this._canvasTouchContactCount + sign * e.changedTouches.length
    );
  };

  this._events.mouseenter = callEvent.bind(this, 'mouseEnter');
  this._events.mouseleave = callEvent.bind(this, 'mouseLeave');

  this._events.mousedown = callEvent.bind(this, 'mouseDown');
  this._events.mousemove = callEvent.bind(this, 'mouseMove');
  this._events.mouseup = callEvent.bind(this, 'mouseUp');

  this._events.touchstart = function (e: TouchEvent) {
    adjustCanvasTouchContacts(e, 1);
    return callEvent.call(_this, 'touchStart', e);
  };
  this._events.touchmove = callEvent.bind(this, 'touchMove');
  this._events.touchend = function (e: TouchEvent) {
    const ret = callEvent.call(_this, 'touchEnd', e);
    adjustCanvasTouchContacts(e, -1);
    return ret;
  };
  this._events.touchcancel = function (e: TouchEvent) {
    const ret = callEvent.call(_this, 'touchEnd', e);
    adjustCanvasTouchContacts(e, -1);
    return ret;
  };

  this._events.pointerdown = function (e) {
    normalizePointerEventForCanvas(e);
    markTouchPointer(e, true);
    return callEvent.call(_this, 'mouseDown', e);
  };
  this._events.pointermove = function (e) {
    normalizePointerEventForCanvas(e);
    return callEvent.call(_this, 'mouseMove', e);
  };
  this._events.pointerup = function (e) {
    normalizePointerEventForCanvas(e);
    markTouchPointer(e, false);
    return callEvent.call(_this, 'mouseUp', e);
  };
  this._events.pointercancel = function (e) {
    normalizePointerEventForCanvas(e);
    markTouchPointer(e, false);
    return callEvent.call(_this, 'mouseUp', e);
  };

  this._events.dblclick = callEvent.bind(this, 'dblclick');
  this._events.mouseScroll = function (e) {
    if (hasAnyActiveCanvasTouch()) {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      if (e && e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
      return;
    }
    return callEvent.call(_this, 'mouseScroll', e);
  };
  this._events.keydown = callEvent.bind(this, 'keyDown');
};

const passiveOptsFalse: any = { passive: false };

var eventsSubscribe = function (subscribe, element) {
  var funcName = subscribe ? 'addEventListener' : 'removeEventListener';

  if (this._pointerEventMode) {
    element[funcName]('pointerdown', this._events.pointerdown, false);
    element[funcName]('pointermove', this._events.pointermove, passiveOptsFalse);
    element[funcName]('pointerup', this._events.pointerup, false);
    element[funcName]('pointercancel', this._events.pointercancel, false);
    element[funcName]('mouseenter', this._events.mouseenter, false);
    element[funcName]('mouseleave', this._events.mouseleave, false);
    element[funcName]('dblclick', this._events.dblclick, false);
    element[funcName]('mousewheel', this._events.mouseScroll, false);
    element[funcName]('wheel', this._events.mouseScroll, false);
    element[funcName]('DOMMouseScroll', this._events.mouseScroll, false);
    window[funcName]('keydown', this._events.keydown, false);
    return;
  }

  element[funcName]('mouseup', this._events.mouseup, false);
  element[funcName]('mousemove', this._events.mousemove, false);
  element[funcName]('mouseenter', this._events.mouseenter, false);
  element[funcName]('mouseleave', this._events.mouseleave, false);
  element[funcName]('mousedown', this._events.mousedown, false);
  element[funcName]('touchstart', this._events.touchstart, passiveOptsFalse);
  element[funcName]('touchmove', this._events.touchmove, passiveOptsFalse);
  element[funcName]('touchend', this._events.touchend, false);
  element[funcName]('touchcancel', this._events.touchcancel, false);
  element[funcName]('dblclick', this._events.dblclick, false);
  element[funcName]('mousewheel', this._events.mouseScroll, false);
  element[funcName]('wheel', this._events.mouseScroll, false);
  element[funcName]('DOMMouseScroll', this._events.mouseScroll, false);
  window[funcName]('keydown', this._events.keydown, false);
};

export class EventBus {
  components = [];
  private _isInitialized = false;
  private _componentsCount = 0;
  private _events = {};
  private _enable = false;
  private _canvas = [];
  /** true → только pointer* на canvas (см. CANVAS_USES_POINTER_EVENTS). */
  private _pointerEventMode = false;

  addComponent(component) {
    this.components.push(component);
    this._componentsCount = this.components.length;
  }

  addComponents(components) {
    for (var i = 0; i < components.length; i++) {
      this.components.push(components[i]);
    }
    this._componentsCount = this.components.length;
  }

  init() {
    if (!this._isInitialized) {
      this._isInitialized = true;
      prepareEventFuncs.call(this);
      this.enable();
    }
  }

  registerEvents(canvas) {
    if (this._canvas.indexOf(canvas) === -1) {
      this._canvas.push(canvas);
      eventsSubscribe.call(this, true, canvas);
    }
  }

  dispose() {
    if (this._isInitialized) {
      this._isInitialized = false;

      for (var i = 0; i < this._canvas.length; i++) {
        eventsSubscribe.call(this, false, this._canvas[i]);
      }

      var components = this.components;
      for (var i = 0; i < components.length; i++) {
        if (components[i].dispose) components[i].dispose();
      }

      this._canvas.length = 0;
      this.components.length = 0;
      this._events = null;
    }
  }

  disable() {
    this._enable = false;
  }

  enable() {
    this._enable = true;
  }
}



// WEBPACK FOOTER //
// ./src/app/event-bus.ts